/**
 * FlashBook — Sound Engine
 *
 * Extends the capsule sound engine with:
 *  • background music, in the three states a creator can leave it in: a track
 *    chosen from the catalogue (`#/lib/backgroundMusic`), deliberate silence, or
 *    nothing ever chosen — which is the theme's own bed, unchanged, so shipping
 *    a music library did not restyle or silence every book that already exists
 *  • automatic ducking: the bed is faded down hard while a recorded reading take
 *    (or a video's own audio) is playing, and faded back when it ends — see
 *    `#/lib/musicDuck` for how far and how
 *  • reading audio playback — the creator's own recording or upload, and nothing
 *    else: there is no synthesised fallback (see the note above
 *    `stopReadingAudio`)
 *
 * All volumes still respect the shared master volume/mute in sounds.ts
 * (getMasterVolume / getMuted).
 */
import {Platform} from 'react-native'
import {type AudioPlayer} from 'expo-audio'

import {type MusicTrack} from '#/lib/backgroundMusic'
import {duckedLevel, MUSIC_DUCK_LEVEL} from '#/lib/musicDuck'
import {
  musicLevel,
  type MusicVoice,
  playMusicTrack,
  stopMusicPreview,
} from '#/lib/musicPlayback'
import {getMasterVolume, getMuted} from '#/lib/sounds'

// ─── Music themes ─────────────────────────────────────────────────────────────

export type FlashbookMusicProfile = {
  label: string
  /** Web synth base frequency */
  baseHz: number
  waveform: OscillatorType
  /** Note pattern (Hz) used by the web synth fallback */
  pattern: number[]
  /** Native playback-rate variation (pitch feel per theme) */
  playbackRate: number
}

export const FLASHBOOK_MUSIC_THEMES: Record<string, FlashbookMusicProfile> = {
  storybook: {
    label: 'Storybook',
    baseHz: 392,
    waveform: 'sine',
    pattern: [392.0, 329.63, 261.63, 329.63, 392.0, 523.25, 392.0, 329.63],
    playbackRate: 1.0,
  },
  notebook: {
    label: 'Notebook',
    baseHz: 440,
    waveform: 'triangle',
    pattern: [440.0, 554.37, 659.25, 554.37, 440.0, 659.25, 880.0, 659.25],
    playbackRate: 1.05,
  },
  academy: {
    label: 'Academy',
    baseHz: 330,
    waveform: 'sine',
    pattern: [330.0, 392.0, 493.88, 392.0, 330.0, 261.63, 330.0, 392.0],
    playbackRate: 0.95,
  },
  magazine: {
    label: 'Magazine',
    baseHz: 466,
    waveform: 'triangle',
    pattern: [466.16, 587.33, 466.16, 698.46, 587.33, 466.16, 587.33, 698.46],
    playbackRate: 1.08,
  },
  'space-adventure': {
    label: 'Space Adventure',
    baseHz: 220,
    waveform: 'sawtooth',
    pattern: [220.0, 261.63, 329.63, 261.63, 220.0, 174.61, 220.0, 329.63],
    playbackRate: 1.02,
  },
}

export function getMusicProfile(themeId: string): FlashbookMusicProfile {
  return FLASHBOOK_MUSIC_THEMES[themeId] ?? FLASHBOOK_MUSIC_THEMES.storybook
}

// ─── Web Audio music (synth + MP3 loop) ───────────────────────────────────────

let _webCtx: AudioContext | null = null
let _webGain: GainNode | null = null
let _webTimer: ReturnType<typeof setTimeout> | null = null
let _webNextTime = 0
let _webNoteIdx = 0
let _webRunning = false
let _bgThemeId: string | null = null
let _currentDuck = 1 // 1 = full, MUSIC_DUCK_LEVEL = ducked

/**
 * The catalogue track currently playing as the bed, and the level its owner set.
 *
 * A chosen track is not the theme's synth: it is a real recording playing through
 * expo-audio, so it keeps its own voice here. `_trackLevel` is the level *before*
 * ducking — ducking multiplies it rather than replacing it, so unducking returns
 * the bed to where the creator's choice put it and not to a constant.
 */
let _trackVoice: MusicVoice | null = null
let _trackLevel = 0

/**
 * Which start request is the live one.
 *
 * Starting a track is asynchronous, and a book is turned quickly. Without a
 * ticket, leaving a page while its bed is still loading starts music *after* the
 * reader has moved on — the one way a bed can outlive the book that asked for it.
 */
let _musicTicket = 0

function _getWebCtx(): AudioContext | null {
  if (Platform.OS !== 'web') return null
  if (typeof AudioContext === 'undefined') return null
  if (!_webCtx) {
    try {
      _webCtx = new AudioContext()
    } catch {
      return null
    }
  }
  if (_webCtx.state === 'suspended') {
    void _webCtx.resume().catch(() => {})
  }
  return _webCtx
}

function _applyGain() {
  if (!_webGain) return
  const target = _currentDuck * (getMuted() ? 0 : getMasterVolume()) * 0.14
  try {
    _webGain.gain.setTargetAtTime(target, _webCtx!.currentTime, 0.12)
  } catch {
    _webGain.gain.value = target
  }
}

function _scheduleNote(ctx: AudioContext) {
  if (!_webRunning || !_webGain) return
  const profile = getMusicProfile(_bgThemeId ?? 'storybook')
  const pattern = profile.pattern
  const hz = pattern[_webNoteIdx % pattern.length]
  _webNoteIdx++

  const osc = ctx.createOscillator()
  const env = ctx.createGain()
  osc.type = profile.waveform
  osc.frequency.value = hz
  osc.detune.value = -4
  env.gain.setValueAtTime(0, _webNextTime)
  env.gain.linearRampToValueAtTime(0.7, _webNextTime + 0.06)
  env.gain.setValueAtTime(0.7, _webNextTime + 0.3)
  env.gain.exponentialRampToValueAtTime(0.001, _webNextTime + 0.5)
  osc.connect(env)
  env.connect(_webGain)
  osc.start(_webNextTime)
  osc.stop(_webNextTime + 0.52)

  _webNextTime += 0.55
  const msUntilNext = (_webNextTime - ctx.currentTime - 0.08) * 1000
  _webTimer = setTimeout(
    () => {
      if (!_webRunning) return
      _scheduleNote(ctx)
    },
    Math.max(0, msUntilNext),
  )
}

// ─── Native music (expo-audio) ────────────────────────────────────────────────

let _nativePlayer: AudioPlayer | null = null

async function _startNativeMusic(themeId: string) {
  try {
    const {createAudioPlayer} = await import('expo-audio')
    if (!_nativePlayer) {
      const source = require('../../assets/sounds/bg-music.wav')
      const player = createAudioPlayer(source)
      player.setPlaybackRate(getMusicProfile(themeId).playbackRate)
      player.volume = _currentDuck * (getMuted() ? 0 : getMasterVolume()) * 0.5
      player.loop = true
      _nativePlayer = player
      await player.play()
    }
  } catch {
    // expo-audio not available — no native music
  }
}

function _stopNativeMusic() {
  if (_nativePlayer) {
    try {
      _nativePlayer.pause()
      _nativePlayer.remove()
    } catch {}
    _nativePlayer = null
  }
}

// ─── Public music API ─────────────────────────────────────────────────────────

/**
 * Start the bed under a book.
 *
 * `music` is the creator's choice, and the player is the only place its three
 * states collapse back into behaviour:
 *
 *   • a track     — that track is the bed
 *   • `null`      — the creator chose silence, so there is none
 *   • `undefined` — nothing was ever chosen, so the theme plays what it always has
 *
 * An audition running in a studio sheet is stopped first: two pieces of music at
 * once is never what anyone meant by pressing play.
 */
export function startFlashbookMusic(
  themeId: string,
  music?: MusicTrack | null,
) {
  stopMusicPreview()
  stopFlashbookMusic()
  const ticket = ++_musicTicket
  _bgThemeId = themeId
  _currentDuck = 1

  if (music === null) return

  if (music) {
    _trackLevel = musicLevel()
    void playMusicTrack(music, {level: _trackLevel}).then(voice => {
      // The book may have been left, or its music changed, while this loaded.
      if (!voice || ticket !== _musicTicket) {
        voice?.stop()
        return
      }
      _trackVoice = voice
      if (_currentDuck !== 1) voice.setLevel(duckedLevel(_trackLevel))
    })
    return
  }

  if (Platform.OS === 'web') {
    const ctx = _getWebCtx()
    if (!ctx) return
    _webRunning = true
    _webNoteIdx = 0
    _webGain = ctx.createGain()
    _applyGain()
    _webGain.connect(ctx.destination)
    _webNextTime = ctx.currentTime + 0.15
    _scheduleNote(ctx)
  } else {
    void _startNativeMusic(themeId)
  }
}

export function stopFlashbookMusic() {
  // Whatever is loading is no longer wanted, whatever is playing is released.
  _musicTicket++
  _trackVoice?.stop()
  _trackVoice = null
  _webRunning = false
  if (_webTimer) {
    clearTimeout(_webTimer)
    _webTimer = null
  }
  if (_webGain) {
    const g = _webGain
    _webGain = null
    try {
      g.gain.setTargetAtTime(0, _webCtx!.currentTime, 0.15)
      setTimeout(() => {
        try {
          g.disconnect()
        } catch {}
      }, 600)
    } catch {
      try {
        g.disconnect()
      } catch {}
    }
  }
  _stopNativeMusic()
  _bgThemeId = null
}

/**
 * Fade the bed down under a voice.
 *
 * A chosen track is ramped by its own voice; the theme's bed is ramped by the
 * Web Audio gain (and set in one step on native, which has no ramp primitive) —
 * but all three use the same duck level, so "how far the music drops" is one
 * number regardless of what is playing underneath it.
 */
export function duckFlashbookMusic() {
  _currentDuck = MUSIC_DUCK_LEVEL
  _trackVoice?.setLevel(duckedLevel(_trackLevel))
  if (Platform.OS === 'web') {
    _applyGain()
  } else if (_nativePlayer) {
    _nativePlayer.volume =
      MUSIC_DUCK_LEVEL * (getMuted() ? 0 : getMasterVolume()) * 0.5
  }
}

export function unduckFlashbookMusic() {
  _currentDuck = 1
  _trackVoice?.setLevel(_trackLevel)
  if (Platform.OS === 'web') {
    _applyGain()
  } else if (_nativePlayer) {
    _nativePlayer.volume = (getMuted() ? 0 : getMasterVolume()) * 0.5
  }
}

export function isFlashbookMusicRunning() {
  return _webRunning || !!_nativePlayer || !!_trackVoice
}

/** Refresh volumes after the user changes master volume/mute. */
export function refreshFlashbookVolumes() {
  if (_trackVoice) {
    _trackLevel = musicLevel()
    _trackVoice.setLevel(
      _currentDuck === 1 ? _trackLevel : duckedLevel(_trackLevel),
    )
  }
  if (Platform.OS === 'web') {
    _applyGain()
  } else if (_nativePlayer) {
    _nativePlayer.volume =
      _currentDuck * (getMuted() ? 0 : getMasterVolume()) * 0.5
  }
}

// ─── Reading audio (recorded MP3) ─────────────────────────────────────────────

/**
 * Live transport state for a read-aloud.
 *
 * Seconds, because every clip is a recording: it has a length, it can be
 * scrubbed, and the player can show a clock a learner can plan against.
 */
export type ReadingAudioProgress = {
  position: number
  duration: number
}

export type ReadingAudioHandle = {
  stop: () => void
  isPlaying: () => boolean
  /** Polled by the player to drive its progress bar and clock. */
  getProgress: () => ReadingAudioProgress
  /** Jump to `fraction` (0–1) of the clip. */
  seek: (fraction: number) => void
}

const IDLE_PROGRESS: ReadingAudioProgress = {
  position: 0,
  duration: 0,
}

// ─── One speaker at a time ───────────────────────────────────────────────────
//
// Starting any read-aloud stops whatever was already playing. The player that
// was interrupted has to find out, or it would keep showing a running progress
// bar for audio that is no longer in the room.

const _preemptListeners = new Set<() => void>()

/** Told when another player takes the speakers. Returns an unsubscribe. */
export function subscribeReadingPreempted(listener: () => void): () => void {
  _preemptListeners.add(listener)
  return () => {
    _preemptListeners.delete(listener)
  }
}

function _preemptAll() {
  for (const listener of [..._preemptListeners]) listener()
}

let _webAudioEl: HTMLAudioElement | null = null
let _nativeAudioPlayer: AudioPlayer | null = null

/**
 * The one element this app plays a reading through on the web.
 *
 * Reused, and **in the document**, and both halves of that are load-bearing.
 * WebKit will silently refuse to play a media element that is not attached to the
 * page: `play()` resolves nothing, `currentTime` never moves, no error is raised
 * — which is exactly the creator's report of a just-recorded take ("the player
 * appears but pressing play does nothing"). An element that has played once also
 * stays unlocked for the rest of the visit, which is what lets a *rendered*
 * preview start after the offline render has taken its moment.
 *
 * It is hidden by size and opacity rather than `display: none`, which some
 * engines treat as "not rendered" and refuse to play.
 */
function _webAudioElement(): HTMLAudioElement | null {
  if (typeof document === 'undefined' || !document.body) return null
  if (_webAudioEl) return _webAudioEl
  const el = document.createElement('audio')
  el.setAttribute('playsinline', 'true')
  el.preload = 'auto'
  el.style.position = 'fixed'
  el.style.left = '0'
  el.style.bottom = '0'
  el.style.width = '1px'
  el.style.height = '1px'
  el.style.opacity = '0'
  el.style.pointerEvents = 'none'
  document.body.appendChild(el)
  _webAudioEl = el
  return el
}

/**
 * Silence the element without throwing it away.
 *
 * The source is cleared rather than kept, so a clip that has been replaced
 * cannot be heard again by a stale press, and so the next play always sets a
 * fresh `src` — re-assigning the same string is a no-op in every browser, which
 * would leave a replayed clip sitting at its end.
 */
function _stopWebAudioEl() {
  const el = _webAudioEl
  if (!el) return
  try {
    el.pause()
    el.onended = null
    el.onerror = null
    el.currentTime = 0
    el.removeAttribute('src')
    el.load()
  } catch {}
}

async function _stopNativeAudio() {
  if (_nativeAudioPlayer) {
    try {
      _nativeAudioPlayer.pause()
      _nativeAudioPlayer.remove()
    } catch {}
    _nativeAudioPlayer = null
  }
}

/** Plays a recorded reading-audio URL. Music ducks while it plays. */
export function playReadingAudio(
  url: string,
  opts?: {onEnded?: () => void; onError?: () => void},
): ReadingAudioHandle {
  stopReadingAudio()
  _preemptAll()
  duckFlashbookMusic()
  let playing = true

  const finish = (cb?: () => void) => {
    if (!playing) return
    playing = false
    unduckFlashbookMusic()
    cb?.()
  }

  if (Platform.OS === 'web') {
    const el = _webAudioElement()
    if (!el) {
      // No document to play in — a build without a DOM. Nothing to report: the
      // caller treats a failed play as the end of the reading.
      finish(opts?.onError)
      return {
        stop: () => finish(),
        isPlaying: () => false,
        getProgress: () => IDLE_PROGRESS,
        seek: () => {},
      }
    }
    el.volume = getMuted() ? 0 : getMasterVolume()
    el.onended = () => finish(opts?.onEnded)
    el.onerror = () => finish(opts?.onError)

    /**
     * Play, retrying once against the clip's metadata.
     *
     * The result is treated as possibly absent rather than as the promise the
     * types promise: the oldest engines this app runs in return nothing from
     * `play()`, and a clip that plays is a success on those too.
     */
    const start = () => {
      const attempt = el.play() as Promise<void> | undefined
      if (!attempt || typeof attempt.catch !== 'function') return
      void attempt.catch(() => {
        /*
         * One retry, and it is what makes a *rendered* preview audible. Safari
         * refuses `play()` on a source it has not read yet, and the desk's
         * preview arrives straight from an offline render with no metadata at
         * all — so the refusal is about timing, not about the clip.
         */
        const retry = () => {
          const second = el.play() as Promise<void> | undefined
          if (second && typeof second.catch === 'function') {
            void second.catch(() => finish(opts?.onError))
          }
        }
        if (el.readyState >= 2) retry()
        else el.addEventListener('canplay', retry, {once: true})
      })
    }

    el.src = url
    try {
      el.load()
    } catch {}
    start()

    return {
      stop: () => {
        _stopWebAudioEl()
        finish()
      },
      isPlaying: () => playing,
      getProgress: () => ({
        position: Number.isFinite(el.currentTime) ? el.currentTime : 0,
        duration: Number.isFinite(el.duration) ? el.duration : 0,
      }),
      seek: fraction => {
        if (!Number.isFinite(el.duration) || el.duration <= 0) return
        try {
          el.currentTime = Math.max(0, Math.min(1, fraction)) * el.duration
        } catch {}
      },
    }
  }

  void (async () => {
    try {
      const {createAudioPlayer} = await import('expo-audio')
      const player = createAudioPlayer(url)
      _nativeAudioPlayer = player
      player.volume = getMuted() ? 0 : getMasterVolume()
      player.loop = false
      player.addListener('playbackStatusUpdate', st => {
        if (st && st.didJustFinish) {
          _nativeAudioPlayer = null
          finish(opts?.onEnded)
        }
      })
      await player.play()
    } catch {
      finish(opts?.onError)
    }
  })()

  return {
    stop: () => {
      void _stopNativeAudio()
      finish()
    },
    isPlaying: () => playing,
    getProgress: () => {
      const p = _nativeAudioPlayer
      if (!p) return IDLE_PROGRESS
      const pos = typeof p.currentTime === 'number' ? p.currentTime : 0
      const dur = typeof p.duration === 'number' ? p.duration : 0
      return {position: pos, duration: dur}
    },
    seek: fraction => {
      const p = _nativeAudioPlayer
      if (!p) return
      const dur = typeof p.duration === 'number' ? p.duration : 0
      if (dur <= 0) return
      void p.seekTo(Math.max(0, Math.min(1, fraction)) * dur).catch(() => {})
    },
  }
}

// ─── No second voice ─────────────────────────────────────────────────────────
//
// A read-aloud is the *creator's* voice, recorded in the studio or uploaded as a
// file, and that is the only audio this engine plays. There used to be a synthesis
// fallback underneath a passage nobody had narrated, and removing it is the point
// rather than a simplification: the device's own speech is not the author, cannot
// be edited or levelled against the rest of a book the way a take can, is a
// different voice on every machine and in every locale, and reads notation as
// characters. A passage the creator never narrated is a passage to read — so the
// play surfaces ask whether a clip exists before they draw a transport at all
// (see `offersReadAloud` in `#/lib/spokenKinds`), and nothing reaches this file
// without one.

/** Stops any current reading audio (music un-ducks). */
export function stopReadingAudio() {
  _stopWebAudioEl()
  void _stopNativeAudio()
  unduckFlashbookMusic()
}

/** Stops everything (music + reading audio). Call on player unmount. */
export function stopAllFlashbookAudio() {
  stopReadingAudio()
  stopFlashbookMusic()
}
